import React, { useEffect, useMemo, useRef } from 'react';
import './ProgressionGuidePanel.css';

const WORLD_TIER_ROWS = [
  { id: 0, name: 'Normal', icon: '—', level: 1, hp: '1.0x', xp: '1.0x', gold: '1.0x', magicFind: '+0', trophy: '1.0x', recipe: '1.0x' },
  { id: 1, name: 'Adventurer', icon: '⚔️', level: 30, hp: '1.3x', xp: '1.25x', gold: '1.2x', magicFind: '+1', trophy: '1.5x', recipe: '1.5x' },
  { id: 2, name: 'Veteran', icon: '🛡️', level: 50, hp: '1.6x', xp: '1.5x', gold: '1.4x', magicFind: '+2', trophy: '2.0x', recipe: '2.0x' },
  { id: 3, name: 'Champion', icon: '🏆', level: 70, hp: '2.0x', xp: '1.8x', gold: '1.65x', magicFind: '+3', trophy: '2.5x', recipe: '2.5x' },
  { id: 4, name: 'Heroic', icon: '💎', level: 90, hp: '2.5x', xp: '2.2x', gold: '2.0x', magicFind: '+4', trophy: '3.0x', recipe: '3.0x' },
  { id: 5, name: 'Mythic', icon: '🔥', level: 110, hp: '3.0x', xp: '2.75x', gold: '2.5x', magicFind: '+5', trophy: '4.0x', recipe: '4.0x' },
];

const ROADMAP_STEPS = [
  {
    title: 'Levels 1-9: Learn the world',
    points: [
      'Explore out of Antaria, follow road connections, and fill out your Atlas.',
      'Use taverns for early bounties and the Deck / Skill Tree to stabilize combat.',
      'Elite monsters already matter here because they can start dropping trophies and better treasure.'
    ]
  },
  {
    title: 'Level 10+: Start The Delve',
    points: [
      'The Delve opens once you are level 10 and have explored at least 10 areas.',
      'Use it to push faster leveling, earn forge tokens, and chase stronger treasure drops and boss rewards.',
      'Win streaks make Delves harder, but long streaks improve your XP, token, and item payouts if you can keep them going.'
    ]
  },
  {
    title: 'Level 30+: Turn on World Tiers',
    points: [
      'Adventurer Tier is the first major progression breakpoint.',
      'World Tiers are voluntary difficulty you can raise or lower at will.',
      'Higher tiers improve XP, gold, recipe drops, trophy value, enchant floors, and elite pressure.'
    ]
  },
  {
    title: 'Levels 50-90: Build-defining chase',
    points: [
      'This is where set gear, trophy crafting, and boss uniques become a long-term target.',
      'Use the Atlas to move into higher-level zones instead of farming a single safe route forever.',
      'Punching up, bounties, and Delve streaking all start paying off more as your baseline power rises.'
    ]
  },
  {
    title: 'Level 110+: Mythic loop',
    points: [
      'Mythic is the final World Tier and the highest reward multiplier package.',
      'The core loop becomes: push harder zones, farm trophies and recipes, improve gear sets, and convert challenge into better loot efficiency.',
      'The guide below is designed to stay useful here as a quick-reference sheet.'
    ]
  }
];

const TROPHIES = [
  'Elite Pelt',
  "Commander's Insignia",
  'Spectral Essence',
  'Elemental Core',
  'Venom Sac',
  'Fey Dust',
  'Dragon Scale',
  'Condensed Slime Core',
  "Giant's Knucklebone",
  'Arcane Cog',
  'Aberrant Cortex',
  'Planar Shard',
  'Heartwood Amber'
];

const SET_FAMILIES = [
  { name: 'Dragonscale', summary: 'Block, damage reduction, and raw combat power.' },
  { name: 'Shadow', summary: 'Turn-one draw, AP tempo, and evasive pressure.' },
  { name: 'Iron Guardian', summary: 'Tank package: block, DR, thorns, and durability.' },
  { name: 'Arcane', summary: 'MP sustain, free spell windows, draw, and caster tempo.' },
  { name: 'Berserker', summary: 'Aggressive ATT scaling, draw, AP, and all-in damage.' },
  { name: "Nature's Grace", summary: 'Healing, MP sustain, DR, and steady survival value.' }
];

const CHALLENGE_CARDS = [
  {
    title: 'Delve Streaks',
    points: [
      '+10% monster difficulty per streak level.',
      '+3% chest gold and +8% chest XP per streak.',
      '+1 extra chest forge token per 2 streaks.',
      'Streak 3+: guaranteed chest item drop.',
      'Streak 5+: chest item quality upgrades to the chest tier.'
    ]
  },
  {
    title: 'Bounties',
    points: [
      'Daily bounty tokens scale with target level: 0 tokens (below Lv10), 1 token (Lv10–24), 2 tokens (Lv25+).',
      'Weekly bounty tokens: 1 token (below Lv10), 3 tokens (Lv10–24), 5 tokens (Lv25+).',
      'Use tavern boards when you want directed goals instead of open-ended farming.'
    ]
  },
  {
    title: 'Punching Up',
    points: [
      'Fighting monsters 3+ levels above you triggers a bonus on XP and gold.',
      'Bonus is +5% per level above you, starting at +5% (3 levels up) and capped at +50% (12+ levels up).',
      'Watch out: at 6+ levels above you, your damage starts being reduced (see the Combat Guide level penalty), so the risk/reward tightens fast.',
      'If a fight shows “Punching Up!” on the result screen, you were paid extra for taking the risk.'
    ]
  }
];

const ATLAS_TIPS = [
  'The Atlas is your route planner, not just a map. Use it to spot which zones you have neglected and which level bands are ahead of you.',
  'Each zone shows level range, discovery progress, connections, and major features like taverns, shops, temples, and Delve entrances.',
  'If you feel under-rewarded, the Atlas is usually the fastest way to identify that you should move to a higher-value region rather than keep grinding a solved one.',
  'Use discovered percentages as a completion target when you want exploration goals beyond raw leveling.'
];

const SECTION_ORDER = [
  { id: 'overview', label: 'Overview' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'world-tiers', label: 'World Tiers' },
  { id: 'endgame-loot', label: 'Loot & Crafting' },
  { id: 'challenge-loops', label: 'Challenge Loops' },
  { id: 'atlas', label: 'Atlas' },
];

const tierColorById = {
  0: '#aaaaaa',
  1: '#4fc3f7',
  2: '#66bb6a',
  3: '#ffa726',
  4: '#ab47bc',
  5: '#ef5350',
};

export default function ProgressionGuidePanel({ onClose, initialSection = 'overview' }) {
  const sectionRefs = useRef({});
  const scrollRef = useRef(null);

  const jumpToSection = useMemo(() => (sectionId) => {
    const node = sectionRefs.current[sectionId];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const id = initialSection || 'overview';
    const timer = window.requestAnimationFrame(() => {
      const node = sectionRefs.current[id];
      if (node) node.scrollIntoView({ behavior: 'auto', block: 'start' });
      else if (scrollRef.current) scrollRef.current.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(timer);
  }, [initialSection]);

  return (
    <div className="pg-overlay" onClick={onClose}>
      <div className="pg-panel" onClick={(event) => event.stopPropagation()}>
        <div className="pg-header">
          <div>
            <h2 className="pg-title">📘 Progression Guide</h2>
            <p className="pg-subtitle">A roadmap for new players and a quick-reference sheet for veteran players.</p>
          </div>
          <button className="pg-close" onClick={onClose}>✕</button>
        </div>

        <div className="pg-jumpbar">
          {SECTION_ORDER.map((section) => (
            <button
              key={section.id}
              className={`pg-jump-btn ${initialSection === section.id ? 'active' : ''}`}
              onClick={() => jumpToSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div className="pg-body" ref={scrollRef}>
          <section className="pg-section pg-hero" ref={(node) => { sectionRefs.current.overview = node; }}>
            <div className="pg-hero-copy">
              <h3>How progression works</h3>
              <p>
                Loria now has a clearer long-term loop: <strong>raise difficulty</strong>, <strong>earn sharper rewards</strong>,
                <strong> assemble stronger builds</strong>, then <strong>push into harder content</strong>. The four major systems are:
                World Tiers, Endgame Loot, Challenge Reward Loops, and the Atlas.
              </p>
            </div>
            <div className="pg-hero-grid">
              <div className="pg-hero-card">
                <span className="pg-hero-icon">🌍</span>
                <strong>World Tiers</strong>
                <span>Voluntary difficulty with scaling rewards.</span>
              </div>
              <div className="pg-hero-card">
                <span className="pg-hero-icon">⚒️</span>
                <strong>Endgame Loot</strong>
                <span>Trophies, set gear, uniques, and recipes worth chasing.</span>
              </div>
              <div className="pg-hero-card">
                <span className="pg-hero-icon">🔥</span>
                <strong>Challenge Loops</strong>
                <span>Delve streaking, punching up, and better bounty payouts.</span>
              </div>
              <div className="pg-hero-card">
                <span className="pg-hero-icon">🗺️</span>
                <strong>Atlas</strong>
                <span>A visual zone roadmap so you always know what is next.</span>
              </div>
            </div>
          </section>

          <section className="pg-section" ref={(node) => { sectionRefs.current.roadmap = node; }}>
            <h3>Quick Roadmap</h3>
            <div className="pg-roadmap">
              {ROADMAP_STEPS.map((step) => (
                <div key={step.title} className="pg-roadmap-card">
                  <h4>{step.title}</h4>
                  {step.points.map((point) => (
                    <p key={point}>{point}</p>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="pg-section" ref={(node) => { sectionRefs.current['world-tiers'] = node; }}>
            <h3>World Tiers</h3>
            <p>
              World Tiers are optional difficulty settings. Raise them when your build is stable and you want better rewards.
              Lower them if you want a safer farming pass. The important point is that they are <strong>voluntary</strong>, so you can tune risk to your current goal.
            </p>
            <div className="pg-table-wrap">
              <table className="pg-table">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Unlock</th>
                    <th>Monster HP</th>
                    <th>XP</th>
                    <th>Gold</th>
                    <th>Magic Find</th>
                    <th>Trophy Value</th>
                    <th>Recipe Drops</th>
                  </tr>
                </thead>
                <tbody>
                  {WORLD_TIER_ROWS.map((tier) => (
                    <tr key={tier.id}>
                      <td style={{ color: tierColorById[tier.id] }}><strong>{tier.icon} {tier.name}</strong></td>
                      <td>Lv {tier.level}</td>
                      <td>{tier.hp}</td>
                      <td>{tier.xp}</td>
                      <td>{tier.gold}</td>
                      <td>{tier.magicFind}</td>
                      <td>{tier.trophy}</td>
                      <td>{tier.recipe}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pg-callout info">
              <strong>When should you tier up?</strong>
              <span>
                Tier up when basic fights are consistent, your potions are lasting, and you are no longer excited by the drops you are seeing.
                If you are chain-farming without danger, you are usually leaving value on the table.
              </span>
            </div>
          </section>

          <section className="pg-section" ref={(node) => { sectionRefs.current['endgame-loot'] = node; }}>
            <h3>Endgame Loot & Crafting</h3>
            <p>
              Endgame loot gives you gear goals beyond simple stat bumps. Elite monsters, recipes, crafted sets,
              and boss uniques all feed into long-term build planning.
            </p>
            <div className="pg-split-grid">
              <div className="pg-card">
                <h4>Elite Trophies</h4>
                <p>Each monster family can drop its own trophy. Those trophies are now a real progression currency instead of dead-end flavor loot.</p>
                <div className="pg-tag-list">
                  {TROPHIES.map((trophy) => <span key={trophy} className="pg-tag">{trophy}</span>)}
                </div>
              </div>
              <div className="pg-card">
                <h4>Set Gear</h4>
                <p>Matching pieces unlock stronger bonuses as your set count rises. High thresholds become fellowship-supportive auras.</p>
                {SET_FAMILIES.map((set) => (
                  <div key={set.name} className="pg-mini-row">
                    <strong>{set.name}</strong>
                    <span>{set.summary}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="pg-checklist">
              <div className="pg-check-item">Trophy crafting recipes use trophies plus forge tokens.</div>
              <div className="pg-check-item">World Tier multiplies trophy sell value and recipe drop frequency.</div>
              <div className="pg-check-item">Boss-specific unique legendaries exist as premium chase drops.</div>
              <div className="pg-check-item">Recipes and trophies now scale far better when you stop farming below your power ceiling.</div>
            </div>
          </section>

          <section className="pg-section" ref={(node) => { sectionRefs.current['challenge-loops'] = node; }}>
            <h3>Challenge Scaling & Reward Loops</h3>
            <p>
              Harder content pays more clearly here. If you take on extra risk, these systems are designed to reward that push with better returns.
            </p>
            <div className="pg-challenge-grid">
              {CHALLENGE_CARDS.map((card) => (
                <div key={card.title} className="pg-card">
                  <h4>{card.title}</h4>
                  {card.points.map((point) => (
                    <p key={point}>{point}</p>
                  ))}
                </div>
              ))}
            </div>
            <div className="pg-callout success">
              <strong>Best use case:</strong>
              <span>
                If your build is good enough to survive harder content but not yet good enough to trivialize it, that is the sweet spot.
                That is where Delve streaking, higher-tier farming, and punching-up bonuses stack into the strongest returns.
              </span>
            </div>
            <div className="pg-reference-grid">
              <div className="pg-reference-card">
                <h4>Delve Titles</h4>
                <p>1: Delve Survivor</p>
                <p>2: Delve Veteran</p>
                <p>3: Delve Champion</p>
                <p>5: Delve Conqueror</p>
                <p>7: Delve Legend</p>
                <p>10: Delve Immortal</p>
              </div>
              <div className="pg-reference-card">
                <h4>Forge Token Sources</h4>
                <p>Bounties now pay tokens directly.</p>
                <p>Delve chests scale tokens with streak, and boss clears remain one of the best big token payouts.</p>
                <p>Some high-end events and systems also award them, but these are the most reliable loops.</p>
              </div>
            </div>
          </section>

          <section className="pg-section" ref={(node) => { sectionRefs.current.atlas = node; }}>
            <h3>Atlas</h3>
            <p>
              The Atlas is the feature that makes the rest of the progression legible. It shows where you are, what zones connect to what, what level range each region expects, and how much of each region you have actually seen.
            </p>
            <div className="pg-atlas-grid">
              <div className="pg-card">
                <h4>What the Atlas tells you</h4>
                {ATLAS_TIPS.map((tip) => <p key={tip}>{tip}</p>)}
              </div>
              <div className="pg-card">
                <h4>Good reasons to open it</h4>
                <p>You are not sure which zone should come next.</p>
                <p>You want to compare your level to a target region.</p>
                <p>You want a Delve entrance, tavern, temple, or a better exploration completion target.</p>
                <p>You feel stalled and need to confirm that the problem is route choice instead of build strength.</p>
              </div>
            </div>
            <div className="pg-callout warning">
              <strong>If progression feels vague, open the Atlas first.</strong>
              <span>
                It is the fastest way to answer “where should I be going?” and helps you choose a better route whenever progress starts to feel unclear.
              </span>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}